import React from "react";
import cs from "classnames";

import { Icon } from "../shared/Icon/Icon";
import { ProductStoreArtwork } from "./ProductStoreArtwork";
import { QuantityStepper } from "./QuantityStepper";
import { PurchaseAddOn } from "./productStorePurchase";

interface Props {
  addOn: PurchaseAddOn;
  selected: boolean;
  /** How many are ordered, following the add-on's quantity rule. */
  quantity: number;
  /** The main product's unit, e.g. "devices", for add-ons that match it. */
  mainLabel?: string;
  /** The selected add-on that needs this one; it can't be cleared. */
  lockedBy?: string;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
}

export const ProductStoreCartAddOn = ({
  addOn,
  selected,
  quantity,
  mainLabel,
  lockedBy,
  onToggle,
  onQuantityChange,
}: Props) => {
  const inputId = `product-store-add-on-${addOn.id}`;
  const lockNoteId = `${inputId}-lock`;

  return (
    <div
      className={cs("ProductStoreCartAddOn", {
        "ProductStoreCartAddOn--selected": selected,
        "ProductStoreCartAddOn--locked": !!lockedBy,
      })}
    >
      <div className="ProductStoreCartAddOn__top">
        {addOn.recommended && (
          <div className="ProductStoreCartAddOn__badge">
            <Icon icon="star-icon" />
            Recommended
          </div>
        )}
        <input
          type="checkbox"
          id={inputId}
          className="ProductStoreCartAddOn__checkbox"
          checked={selected}
          disabled={!!lockedBy}
          aria-describedby={lockedBy ? lockNoteId : undefined}
          onChange={onToggle}
        />
      </div>
      <div className="ProductStoreCartAddOn__heading">
        <div
          className={cs(
            "ProductStoreCartAddOn__media",
            `ProductStoreCartAddOn__media--${addOn.artwork}`
          )}
        >
          <ProductStoreArtwork artwork={addOn.artwork} alt={addOn.artworkAlt} />
        </div>
        {/* Stretched over the card, so clicking anywhere toggles it. */}
        <label htmlFor={inputId} className="stretched-link">
          {addOn.title}
        </label>
      </div>
      {addOn.highlights.length > 0 && (
        <ul className="ProductStoreCartAddOn__highlights">
          {addOn.highlights.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
      )}
      {lockedBy && (
        <p id={lockNoteId} className="ProductStoreCartAddOn__lockNote">
          Required with {lockedBy}
        </p>
      )}
      <div className="ProductStoreCartAddOn__footer">
        <p className="ProductStoreCartAddOn__price">
          <strong>{addOn.unitPrice}</strong> {addOn.unitLabel}
        </p>
        {selected && addOn.quantity === "matchMain" && mainLabel && (
          <span className="ProductStoreCartAddOn__matched">
            × {quantity}, matches your {mainLabel}
          </span>
        )}
        {selected && addOn.quantity === "independent" && (
          <div className="ProductStoreCartAddOn__quantity">
            {addOn.quantityLabel && <span>{addOn.quantityLabel}</span>}
            <QuantityStepper
              label={addOn.title}
              value={quantity}
              onChange={onQuantityChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};
